import React, { useEffect, useState } from 'react';
import './GenerationProgress.css';

interface GenerationProgressProps {
  totalCount: number;
  currentBatch: number;
  totalBatches: number;
  generatedCount: number;
  failedCount: number;
  isComplete: boolean;
  message?: string;
}

const GenerationProgress: React.FC<GenerationProgressProps> = ({
  totalCount,
  currentBatch,
  totalBatches,
  generatedCount,
  failedCount,
  isComplete,
  message
}) => {
  const [dots, setDots] = useState('');
  const progress = totalCount > 0 ? ((generatedCount + failedCount) / totalCount) * 100 : 0;
  
  // Animated loading dots
  useEffect(() => {
    if (!isComplete) {
      const interval = setInterval(() => {
        setDots(prev => prev.length >= 3 ? '' : prev + '.');
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isComplete]);

  // Estimate time remaining (5-7 seconds per batch)
  const remainingBatches = totalBatches - currentBatch;
  const estimatedSeconds = remainingBatches * 6;
  const estimatedTime = estimatedSeconds > 60 
    ? `${Math.ceil(estimatedSeconds / 60)} minute${Math.ceil(estimatedSeconds / 60) > 1 ? 's' : ''}`
    : `${estimatedSeconds} seconds`;

  return (
    <div className="generation-progress-overlay">
      <div className="generation-progress-container">
        {/* AI Brain Animation */}
        <div className="ai-brain-container">
          <svg className="ai-brain" viewBox="0 0 200 200" width="120" height="120">
            {/* Brain outline */}
            <path
              d="M100 40 C 60 40, 40 70, 40 100 C 40 130, 60 160, 100 160 C 140 160, 160 130, 160 100 C 160 70, 140 40, 100 40"
              fill="none"
              stroke="url(#brainGradient)"
              strokeWidth="3"
              className="brain-outline"
            />
            
            {/* Neural connections */}
            <g className="neurons">
              {[...Array(8)].map((_, i) => {
                const angle = (i * 45) * Math.PI / 180;
                const x1 = 100 + 30 * Math.cos(angle);
                const y1 = 100 + 30 * Math.sin(angle);
                const x2 = 100 + 50 * Math.cos(angle);
                const y2 = 100 + 50 * Math.sin(angle);
                
                return (
                  <g key={i}>
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="url(#neuronGradient)"
                      strokeWidth="2"
                      className={`neuron-line neuron-${i}`}
                    />
                    <circle
                      cx={x2}
                      cy={y2}
                      r="4"
                      fill="url(#neuronGradient)"
                      className={`neuron-dot neuron-${i}`}
                    />
                  </g>
                );
              })}
            </g>
            
            {/* Gradient definitions */}
            <defs>
              <linearGradient id="brainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>
              <linearGradient id="neuronGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f093fb" />
                <stop offset="100%" stopColor="#f5576c" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Progress Information */}
        <div className="generation-info">
          <h3 className="generation-title">
            {isComplete ? 'Generation Complete!' : `Generating Problems with AI${dots}`}
          </h3>
          
          {!isComplete && (
            <p className="generation-status">
              Processing batch {currentBatch} of {totalBatches}
            </p>
          )}
          
          {/* Progress Bar */}
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="progress-text">{Math.round(progress)}%</span>
          </div>

          {/* Statistics */}
          <div className="generation-stats">
            <div className="stat-item success">
              <span className="stat-icon">✓</span>
              <span className="stat-label">Generated:</span>
              <span className="stat-value">{generatedCount}</span>
            </div>
            {failedCount > 0 && (
              <div className="stat-item error">
                <span className="stat-icon">✗</span>
                <span className="stat-label">Failed:</span>
                <span className="stat-value">{failedCount}</span>
              </div>
            )}
            <div className="stat-item pending">
              <span className="stat-icon">⏱</span>
              <span className="stat-label">Remaining:</span>
              <span className="stat-value">{totalCount - generatedCount - failedCount}</span>
            </div>
          </div>

          {/* Time estimate */}
          {!isComplete && remainingBatches > 0 && (
            <p className="time-estimate">
              Estimated time remaining: {estimatedTime}
            </p>
          )}

          {/* Message */}
          {message && (
            <p className={`generation-message ${isComplete ? 'complete' : ''}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerationProgress;